import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, DollarSign, Package, CreditCard, CheckCircle, XCircle, Handshake, Loader2, AlertCircle, User, Star } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { negotiationService } from '../../../services/negotiationService';
import { ratingService } from '../../../services/ratingService';
import RatingModal from './RatingModal';

const STATUS_LABELS = {
  initiated: 'Pokrenuto',
  in_progress: 'U toku',
  agreed: 'Dogovoreno',
  completed: 'Završeno',
  cancelled: 'Otkazano',
};

const STATUS_COLORS = {
  initiated: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-orange-100 text-orange-700',
  agreed: 'bg-green-100 text-green-700',
  completed: 'bg-teal-100 text-teal-700',
  cancelled: 'bg-red-100 text-red-700',
};

function formatTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr)?.toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr)?.toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function MessageBubble({ message, isOwn }) {
  const isOffer = message?.messageType === 'offer' || message?.messageType === 'counter_offer';
  const isSystem = message?.messageType === 'system';

  if (isSystem) {
    return (
      <div className="flex justify-center my-2">
        <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
          {message?.messageContent}
        </span>
      </div>
    );
  }

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}>
      {!isOwn && (
        <div className="w-7 h-7 rounded-full bg-teal-100 flex items-center justify-center mr-2 flex-shrink-0 mt-1">
          {message?.senderAvatar ? (
            <img src={message?.senderAvatar} alt={message?.senderName} className="w-7 h-7 rounded-full object-cover" />
          ) : (
            <User size={14} className="text-teal-600" />
          )}
        </div>
      )}
      <div className={`max-w-[75%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
        {!isOwn && (
          <span className="text-xs text-gray-500 mb-1 ml-1">{message?.senderName}</span>
        )}
        <div
          className={`rounded-2xl px-4 py-2.5 ${
            isOffer
              ? 'rounded-xl border'
              : isOwn
              ? 'bg-teal-700 text-white' : 'bg-white border border-gray-200 text-gray-800'
          }`}
          style={isOffer ? {backgroundColor:'#FAF5DC', borderColor:'#F5EBB9'} : {}}
        >
          {isOffer && message?.offerDetails && (
            <div className="mb-2">
              <p className="text-xs font-semibold mb-1.5" style={{color:'#9A8226'}}>
                {message?.messageType === 'counter_offer' ? '↩ Kontra-ponuda' : '📋 Predlog uslova'}
              </p>
              <div className="space-y-1 text-xs">
                {message?.offerDetails?.price && (
                  <div className="flex items-center gap-1.5" style={{color:'#7C681E'}}>
                    <DollarSign size={11} />
                    <span>Cena: <strong>{Number(message?.offerDetails?.price)?.toLocaleString('sr-RS')} {message?.offerDetails?.currency || 'RSD'}</strong></span>
                  </div>
                )}
                {message?.offerDetails?.quantity && (
                  <div className="flex items-center gap-1.5" style={{color:'#7C681E'}}>
                    <Package size={11} />
                    <span>Količina: <strong>{message?.offerDetails?.quantity}</strong></span>
                  </div>
                )}
                {message?.offerDetails?.paymentMethod && (
                  <div className="flex items-center gap-1.5" style={{color:'#7C681E'}}>
                    <CreditCard size={11} />
                    <span>Plaćanje: <strong>{message?.offerDetails?.paymentMethod}</strong></span>
                  </div>
                )}
              </div>
            </div>
          )}
          <p className={`text-sm leading-relaxed ${
            isOffer ? '' : isOwn ? 'text-white' : 'text-gray-800'
          }`} style={isOffer ? {color:'#5E4E16'} : {}}>
            {message?.messageContent}
          </p>
        </div>
        <span className={`text-xs text-gray-400 mt-1 ${isOwn ? 'mr-1' : 'ml-1'}`}>
          {formatTime(message?.createdAt)}
        </span>
      </div>
    </div>
  );
}

export default function NegotiationChat({ negotiation, currentUserId, onClose, onStatusChange }) {
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState('');
  const [showProposalForm, setShowProposalForm] = useState(false);
  const [proposal, setProposal] = useState({ price: '', quantity: '', paymentMethod: '', note: '' });
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [negStatus, setNegStatus] = useState(negotiation?.status || 'initiated');
  const [closeRequestedBy, setCloseRequestedBy] = useState(negotiation?.closeRequestedBy || null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [hasRated, setHasRated] = useState(false);
  const messagesEndRef = useRef(null);
  const channelRef = useRef(null);
  const statusChannelRef = useRef(null);

  const isClosed = negStatus === 'completed' || negStatus === 'cancelled';
  const iAmRequester = closeRequestedBy === currentUserId;
  const otherPartyRequested = closeRequestedBy && closeRequestedBy !== currentUserId;

  const scrollToBottom = useCallback(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!negotiation?.id) return;
    loadMessages();
    markRead();
    subscribeRealtime();
    subscribeStatusChanges();
    // Check if already rated
    ratingService?.hasRated(negotiation?.id)?.then(rated => {
      setHasRated(rated);
      // If negotiation is already completed and user hasn't rated, prompt them
      const currentStatus = negotiation?.status || '';
      const isAlreadyCompleted = currentStatus === 'completed';
      if (isAlreadyCompleted && !rated) {
        setShowRatingModal(true);
      }
    });
    return () => {
      if (channelRef?.current) supabase?.removeChannel(channelRef?.current);
      if (statusChannelRef?.current) supabase?.removeChannel(statusChannelRef?.current);
    };
  }, [negotiation?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Sync close_requested_by from negotiation prop
  useEffect(() => {
    setCloseRequestedBy(negotiation?.closeRequestedBy || null);
  }, [negotiation?.closeRequestedBy]);

  const loadMessages = async () => {
    setLoadingMessages(true);
    try {
      const msgs = await negotiationService?.getMessages(negotiation?.id);
      setMessages(msgs);
    } catch (err) {
      setError('Greška pri učitavanju poruka');
    } finally {
      setLoadingMessages(false);
    }
  };

  const markRead = async () => {
    try {
      await negotiationService?.markMessagesAsRead(negotiation?.id);
    } catch {}
  };

  const subscribeRealtime = () => {
    const channel = negotiationService?.subscribeToMessages(negotiation?.id, async (newMsg) => {
      try {
        const { data } = await supabase
          ?.from('negotiation_messages')
          ?.select(`
            id, negotiation_id, sender_id, message_type, message_content,
            offer_details, is_read, created_at,
            sender:user_profiles!sender_id(id, full_name, profile_picture_url)
          `)
          ?.eq('id', newMsg?.id)
          ?.single();

        if (data) {
          const formatted = {
            id: data?.id,
            negotiationId: data?.negotiation_id,
            senderId: data?.sender_id,
            messageType: data?.message_type,
            messageContent: data?.message_content,
            offerDetails: data?.offer_details,
            isRead: data?.is_read,
            createdAt: data?.created_at,
            senderName: data?.sender?.full_name || 'Korisnik',
            senderAvatar: data?.sender?.profile_picture_url,
          };
          setMessages(prev => {
            if (prev?.find(m => m?.id === formatted?.id)) return prev;
            return [...prev, formatted];
          });
          if (data?.sender_id !== currentUserId) {
            await negotiationService?.markMessagesAsRead(negotiation?.id);
          }
        }
      } catch {}
    });
    channelRef.current = channel;
  };

  const subscribeStatusChanges = () => {
    const channel = supabase
      ?.channel(`neg-deal-status-${negotiation?.id}`)
      ?.on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'negotiations', filter: `id=eq.${negotiation?.id}` },
        (payload) => {
          const updated = payload?.new;
          if (updated?.status) setNegStatus(updated?.status);
          if (updated?.close_requested_by !== undefined) {
            setCloseRequestedBy(updated?.close_requested_by);
          }
          if (updated?.status === 'completed') {
            onStatusChange?.(negotiation?.id, 'completed');
            // Show rating modal if not yet rated
            ratingService?.hasRated(negotiation?.id)?.then(rated => {
              if (!rated) setShowRatingModal(true);
            });
          }
        }
      )
      ?.subscribe();
    statusChannelRef.current = channel;
  };

  const handleSendText = async () => {
    if (!text?.trim() || sending || isClosed) return;
    setSending(true);
    setError('');
    try {
      const msg = await negotiationService?.sendMessage(negotiation?.id, text?.trim());
      setMessages(prev => {
        if (prev?.find(m => m?.id === msg?.id)) return prev;
        return [...prev, msg];
      });
      setText('');
      if (negStatus === 'initiated') setNegStatus('in_progress');
    } catch (err) {
      setError(err?.message || 'Greška pri slanju poruke');
    } finally {
      setSending(false);
    }
  };

  const handleSendProposal = async () => {
    if (sending || isClosed) return;
    if (!proposal?.price && !proposal?.quantity && !proposal?.paymentMethod) {
      setError('Unesite bar jedan uslov predloga');
      return;
    }
    setSending(true);
    setError('');
    try {
      const offerDetails = {};
      if (proposal?.price) offerDetails.price = proposal?.price;
      if (proposal?.quantity) offerDetails.quantity = proposal?.quantity;
      if (proposal?.paymentMethod) offerDetails.paymentMethod = proposal?.paymentMethod;
      offerDetails.currency = negotiation?.currency || 'RSD';

      const noteText = proposal?.note || 'Predlog uslova';
      const msg = await negotiationService?.sendMessage(
        negotiation?.id,
        noteText,
        'offer',
        offerDetails
      );
      setMessages(prev => {
        if (prev?.find(m => m?.id === msg?.id)) return prev;
        return [...prev, msg];
      });
      setProposal({ price: '', quantity: '', paymentMethod: '', note: '' });
      setShowProposalForm(false);
      if (negStatus === 'initiated') setNegStatus('in_progress');
    } catch (err) {
      setError(err?.message || 'Greška pri slanju predloga');
    } finally {
      setSending(false);
    }
  };

  // Request deal close (first party clicks "Zatvori dogovor")
  const handleRequestClose = async () => {
    setActionLoading('close');
    setError('');
    try {
      await ratingService?.requestDealClose(negotiation?.id);
      setCloseRequestedBy(currentUserId);
      // Send system message
      await supabase?.from('negotiation_messages')?.insert({
        negotiation_id: negotiation?.id,
        sender_id: currentUserId,
        message_type: 'system',
        message_content: '🤝 Jedna strana je zatražila zatvaranje dogovora. Čeka se potvrda druge strane.',
      });
      await loadMessages();
    } catch (err) {
      setError(err?.message || 'Greška pri zatvaranju dogovora');
    } finally {
      setActionLoading('');
    }
  };

  // Confirm deal close (second party)
  const handleConfirmClose = async () => {
    setActionLoading('confirm');
    setError('');
    try {
      await ratingService?.confirmDealClose(negotiation?.id);
      setNegStatus('completed');
      setCloseRequestedBy(null);
      onStatusChange?.(negotiation?.id, 'completed');
      // Send system message
      await supabase?.from('negotiation_messages')?.insert({
        negotiation_id: negotiation?.id,
        sender_id: currentUserId,
        message_type: 'system',
        message_content: '✅ Obe strane su potvrdile! Dogovor je uspešno zatvoren. Cena je plaćena i vaučeri su preneti.',
      });
      await loadMessages();
      // Show rating modal
      const alreadyRated = await ratingService?.hasRated(negotiation?.id);
      if (!alreadyRated) setShowRatingModal(true);
    } catch (err) {
      setError(err?.message || 'Greška pri potvrdi dogovora');
    } finally {
      setActionLoading('');
    }
  };

  // Decline deal close request
  const handleDeclineClose = async () => {
    setActionLoading('decline');
    setError('');
    try {
      await ratingService?.declineDealClose(negotiation?.id);
      setCloseRequestedBy(null);
      await supabase?.from('negotiation_messages')?.insert({
        negotiation_id: negotiation?.id,
        sender_id: currentUserId,
        message_type: 'system',
        message_content: '↩ Zahtev za zatvaranje dogovora je odbijen. Pregovaranje se nastavlja.',
      });
      await loadMessages();
    } catch (err) {
      setError(err?.message || 'Greška pri odbijanju zahteva');
    } finally {
      setActionLoading('');
    }
  };

  const handleAction = async (action) => {
    setActionLoading(action);
    setError('');
    try {
      let newStatus;
      let systemMsg;
      if (action === 'accept') {
        newStatus = 'agreed';
        systemMsg = 'Uslovi su prihvaćeni. Pregovaranje je dogovoreno!';
      } else if (action === 'reject') {
        newStatus = 'cancelled';
        systemMsg = 'Pregovaranje je odbijeno.';
      }

      await negotiationService?.updateStatus(negotiation?.id, newStatus);
      await supabase?.from('negotiation_messages')?.insert({
        negotiation_id: negotiation?.id,
        sender_id: currentUserId,
        message_type: 'system',
        message_content: systemMsg,
      });
      setNegStatus(newStatus);
      onStatusChange?.(negotiation?.id, newStatus);
      await loadMessages();
    } catch (err) {
      setError(err?.message || 'Greška pri ažuriranju statusa');
    } finally {
      setActionLoading('');
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendText();
    }
  };

  const handleRated = () => {
    setHasRated(true);
    setShowRatingModal(false);
  };

  return (
    <>
      <div className="flex flex-col h-full bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate text-sm">
              {negotiation?.offerName || 'Pregovaranje'}
            </h3>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-gray-500">{negotiation?.counterpartyName}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS?.[negStatus] || 'bg-gray-100 text-gray-600'}`}>
                {STATUS_LABELS?.[negStatus] || negStatus}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors ml-2">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        {/* Offer summary */}
        {(negotiation?.maxPrice || negotiation?.tokenQuantity) && (
          <div className="px-4 py-2 bg-teal-50 border-b border-teal-100 flex items-center gap-4 text-xs text-teal-700">
            {negotiation?.tokenQuantity && (
              <span className="flex items-center gap-1">
                <Package size={11} /> {negotiation?.tokenQuantity?.toLocaleString('sr-RS')} kom
              </span>
            )}
            {negotiation?.maxPrice && (
              <span className="flex items-center gap-1">
                <DollarSign size={11} /> {Number(negotiation?.maxPrice)?.toLocaleString('sr-RS')} {negotiation?.currency || 'RSD'}
              </span>
            )}
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1" style={{ minHeight: 0 }}>
          {loadingMessages ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 size={24} className="animate-spin text-teal-600" />
            </div>
          ) : messages?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center mb-3">
                <Send size={20} className="text-teal-400" />
              </div>
              <p className="text-gray-500 text-sm">Nema poruka još uvek</p>
              <p className="text-gray-400 text-xs mt-1">Pošaljite prvu poruku ili predlog</p>
            </div>
          ) : (
            messages?.map(msg => (
              <MessageBubble
                key={msg?.id}
                message={msg}
                isOwn={msg?.senderId === currentUserId}
              />
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 mb-2 flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertCircle size={13} />
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto"><X size={12} /></button>
          </div>
        )}

        {/* Deal close confirmation banner — shown to second party */}
        {!isClosed && otherPartyRequested && (
          <div className="mx-4 mb-2 p-3 bg-green-50 border border-green-300 rounded-xl">
            <p className="text-xs font-semibold text-green-800 mb-2 flex items-center gap-1.5">
              <Handshake size={14} /> {negotiation?.counterpartyName} želi da zatvori dogovor
            </p>
            <p className="text-xs text-green-700 mb-3">
              Da li potvrđujete da je cena plaćena i vaučeri preneti?
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleConfirmClose}
                disabled={!!actionLoading}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60"
              >
                {actionLoading === 'confirm' ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={13} />}
                Potvrđujem
              </button>
              <button
                onClick={handleDeclineClose}
                disabled={!!actionLoading}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-red-100 text-red-700 text-xs font-semibold rounded-lg hover:bg-red-200 transition-colors disabled:opacity-60"
              >
                {actionLoading === 'decline' ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={13} />}
                Odbij
              </button>
            </div>
          </div>
        )}

        {/* Waiting banner — shown to requester */}
        {!isClosed && iAmRequester && (
          <div className="mx-4 mb-2 px-3 py-2.5 rounded-xl flex items-center gap-2 text-xs" style={{backgroundColor:'#FAF5DC', border:'1px solid #F5EBB9', color:'#7C681E'}}>
            <Loader2 size={13} className="animate-spin flex-shrink-0" />
            <span>Čeka se potvrda od <strong>{negotiation?.counterpartyName}</strong>...</span>
          </div>
        )}

        {/* Proposal Form */}
        {showProposalForm && !isClosed && (
          <div className="mx-4 mb-2 p-3 rounded-xl" style={{backgroundColor:'#FAF5DC', border:'1px solid #F5EBB9'}}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold" style={{color:'#7C681E'}}>📋 Predlog uslova</p>
              <button onClick={() => setShowProposalForm(false)} style={{color:'#CFB53B'}} className="hover:opacity-70">
                <X size={14} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <label className="text-xs mb-1 block" style={{color:'#9A8226'}}>Cena (RSD)</label>
                <input
                  type="number"
                  value={proposal?.price}
                  onChange={e => setProposal(p => ({ ...p, price: e?.target?.value }))}
                  placeholder="npr. 5000"
                  className="w-full text-xs rounded-lg px-2 py-1.5 bg-white focus:outline-none"
                  style={{border:'1px solid #DFC84D'}}
                />
              </div>
              <div>
                <label className="text-xs mb-1 block" style={{color:'#9A8226'}}>Količina</label>
                <input
                  type="number"
                  value={proposal?.quantity}
                  onChange={e => setProposal(p => ({ ...p, quantity: e?.target?.value }))}
                  placeholder="npr. 100"
                  className="w-full text-xs rounded-lg px-2 py-1.5 bg-white focus:outline-none"
                  style={{border:'1px solid #DFC84D'}}
                />
              </div>
            </div>
            <div className="mb-2">
              <label className="text-xs mb-1 block" style={{color:'#9A8226'}}>Način plaćanja</label>
              <select
                value={proposal?.paymentMethod}
                onChange={e => setProposal(p => ({ ...p, paymentMethod: e?.target?.value }))}
                className="w-full text-xs rounded-lg px-2 py-1.5 bg-white focus:outline-none"
                style={{border:'1px solid #DFC84D'}}
              >
                <option value="">Izaberi...</option>
                <option value="Gotovina">Gotovina</option>
                <option value="Bankarski transfer">Bankarski transfer</option>
                <option value="Kartica">Kartica</option>
                <option value="Kripto">Kripto</option>
                <option value="Po dogovoru">Po dogovoru</option>
              </select>
            </div>
            <div className="mb-2">
              <label className="text-xs mb-1 block" style={{color:'#9A8226'}}>Napomena (opciono)</label>
              <input
                type="text"
                value={proposal?.note}
                onChange={e => setProposal(p => ({ ...p, note: e?.target?.value }))}
                placeholder="Dodatna napomena..."
                className="w-full text-xs rounded-lg px-2 py-1.5 bg-white focus:outline-none"
                style={{border:'1px solid #DFC84D'}}
              />
            </div>
            <button
              onClick={handleSendProposal}
              disabled={sending}
              className="w-full flex items-center justify-center gap-1.5 py-1.5 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-60"
              style={{backgroundColor:'#CFB53B'}}
            >
              {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Pošalji predlog
            </button>
          </div>
        )}

        {/* Action buttons */}
        {!isClosed && !closeRequestedBy && (
          <div className="px-4 pb-2 flex gap-2">
            <button
              onClick={() => handleAction('accept')}
              disabled={!!actionLoading}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60"
            >
              {actionLoading === 'accept' ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={13} />}
              Prihvati
            </button>
            <button
              onClick={() => handleAction('reject')}
              disabled={!!actionLoading}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-red-100 text-red-700 text-xs font-medium rounded-lg hover:bg-red-200 transition-colors disabled:opacity-60"
            >
              {actionLoading === 'reject' ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={13} />}
              Odbij
            </button>
            <button
              onClick={handleRequestClose}
              disabled={!!actionLoading}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-teal-700 text-white text-xs font-medium rounded-lg hover:bg-teal-800 transition-colors disabled:opacity-60"
            >
              {actionLoading === 'close' ? <Loader2 size={12} className="animate-spin" /> : <Handshake size={13} />}
              Zatvori dogovor
            </button>
          </div>
        )}

        {/* Closed state banner */}
        {isClosed && (
          <div className={`mx-4 mb-2 px-3 py-2 rounded-lg text-xs font-medium text-center ${
            negStatus === 'completed'
              ? 'bg-green-50 text-green-700 border border-green-200' :'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {negStatus === 'completed' ? '🤝 Dogovor je zatvoren — obe strane su potvrdile' : '❌ Pregovaranje je odbijeno'}
            {negStatus === 'completed' && !hasRated && (
              <button
                onClick={() => setShowRatingModal(true)}
                className="ml-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-colors"
                style={{backgroundColor:'#F5EBB9', color:'#9A8226'}}
              >
                <Star size={11} /> Oceni
              </button>
            )}
            {negStatus === 'completed' && hasRated && (
              <span className="ml-3 inline-flex items-center gap-1 text-xs" style={{color:'#CFB53B'}}>
                <Star size={11} style={{fill:'#CFB53B', color:'#CFB53B'}} /> Ocenjeno
              </span>
            )}
          </div>
        )}

        {/* Input area */}
        {!isClosed && (
          <div className="px-4 pb-4 pt-2 border-t border-gray-100">
            <div className="flex items-end gap-2">
              <button
                onClick={() => setShowProposalForm(v => !v)}
                className={`flex-shrink-0 p-2 rounded-lg border transition-colors text-xs ${
                  showProposalForm
                    ? 'border' :'border-gray-300 text-gray-500 hover:bg-gray-100'
                }`}
                style={showProposalForm ? {backgroundColor:'#F5EBB9', borderColor:'#DFC84D', color:'#9A8226'} : {}}
                title="Predlog uslova"
              >
                <DollarSign size={16} />
              </button>
              <textarea
                value={text}
                onChange={e => setText(e?.target?.value)}
                onKeyDown={handleKeyDown}
                placeholder="Napišite poruku... (Enter za slanje)"
                rows={1}
                className="flex-1 resize-none border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                style={{ maxHeight: '100px', overflowY: 'auto' }}
              />
              <button
                onClick={handleSendText}
                disabled={!text?.trim() || sending}
                className="flex-shrink-0 p-2.5 bg-teal-700 text-white rounded-xl hover:bg-teal-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Rating Modal */}
      {showRatingModal && (
        <RatingModal
          negotiation={negotiation}
          currentUserId={currentUserId}
          onClose={() => setShowRatingModal(false)}
          onRated={handleRated}
        />
      )}
    </>
  );
}
